import { yupResolver } from '@hookform/resolvers/yup'
import { type ElementType, useEffect, useState, type FormEvent } from 'react'
import { type FieldValues, useForm, FormProvider } from 'react-hook-form'
import { isPresent } from '@jjvgaming/player-payback-library'
import { useApiClient } from 'hooks/useApiClient'
import { useSnackbar } from 'stores/useSnackbar'
import { useNavigate, Link as RouterLink } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Page } from 'components/Shared/Page'
import { PageHeader } from 'components/Shared/PageHeader'
import {
  Alert,
  Box,
  Button,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  type SelectChangeEvent,
  Stack,
  SvgIcon,
  Typography,
} from '@mui/material'
import { TextField } from 'components/Shared/TextField'
import { FormFieldErrorMessage } from 'components/FormFieldErrorMessage'
import { useLayout } from 'hooks/useLayout'
import EventIcon from '@mui/icons-material/Event'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import { HookableDateField } from 'components/Shared/HookableDateField'
import { HookableTimeField } from 'components/Shared/HookableTimeField'
import { formatISO, parse } from 'date-fns'
import {
  badgeRewardNameSchema,
  badgeRewardDescriptionSchema,
  badgeRewardRenewableTypeSchema,
  badgeRewardDependsOnBadgeRewardIdSchema,
  badgeRewardJJPointsAwardedSchema,
  badgeRewardCountSchema,
  newBadgeRewardStartDateSchema,
  newBadgeRewardStartTimeSchema,
  newBadgeRewardEndDateSchema,
  newBadgeRewardEndTimeSchema,
  badgeRewardSchedulesSchema,
  badgeRewardSubtypeSchema,
  badgeRewardCategorySchema,
  locationBadgeTypeSchema,
  badgeRewardLicensedEstablishmentIdsSchema,
  badgeRewardCorporateAccountIdSchema,
  badgeRewardOrganizationIdSchema,
  badgeRewardLimitCheckinsSchema,
  badgeRewardsProgramId,
} from 'utils/schemas/BadgeRewardSchema'
import { ActivityIndicator } from 'components/Shared/ActivityIndicator'
import { ScheduleRow } from 'pages/ClientManagement/LicensedEstablishments/LicensedEstablishmentNewPage/ScheduleRow'
import { daysOfWeek } from 'utils/util'
import { useWatch } from 'react-hook-form'
import {
  type BadgeRewardType,
  type BadgeRewardSchedule,
  type BadgeRewardRenewableType,
  type BadgeSubtype,
  type BadgeLEDTO,
  type RewardsProgram,
} from 'types/api'
import {
  getBadgeRewardTypeName,
  badgeRewardTypeHasAccessToControl,
} from 'utils/badgeRewardsUtils'
import { Counter } from 'components/Counter/Counter'
import { ContestSubtypeSelect as LocationBadgeTypeSelect } from 'components/Shared/ContestSubtypeSelect'
import { SelectCorporateAccount } from 'components/SelectCorporateAccount'
import { SelectFromAllOrganizations } from 'components/SelectFromAllOrganizations'
import { LocationSelect } from 'components/Shared/LocationSelect'
import { useManageBadgeLEs } from 'hooks/api/Contests/BadgeReward/useManageBadgeLEs'
import { object } from 'yup'
import { SearchField } from 'components/SearchField'
import { useTypedApiClient } from 'hooks/useTypedApiClient'
import { type ApiError } from 'openapi-typescript-fetch'
import { useDebounce } from '@uidotdev/usehooks'
import { colorPrimitives } from 'components/Theme'
import { colors } from 'components/ThemeProvider'
import RewardProgramIcon from 'assets/nav_icons/rewards_program.svg?react'
import CloseIcon from 'assets/close-filled.svg?react'
import { useGetAllLocationBadges } from 'hooks/api/Contests/BadgeReward/useGetAllLocationBadges'
import { useLocationScopedSweepstakes } from 'hooks/api/Contests/BadgeReward/useLocationScopedSweepstakes'

const resolveIconComponent = (iconSource: unknown): ElementType => {
  if (iconSource && typeof iconSource === 'object' && 'default' in iconSource) {
    return (iconSource as { default: ElementType }).default
  }

  return iconSource as ElementType
}

export const BadgeRewardSchema = object().shape({
  category: badgeRewardCategorySchema,
  type: locationBadgeTypeSchema,
  subtype: badgeRewardSubtypeSchema,
  name: badgeRewardNameSchema,
  description: badgeRewardDescriptionSchema,
  renewable: badgeRewardRenewableTypeSchema,
  dependsOnBadgeRewardId: badgeRewardDependsOnBadgeRewardIdSchema,
  jjPointsAwarded: badgeRewardJJPointsAwardedSchema,
  count: badgeRewardCountSchema,
  limitCheckins: badgeRewardLimitCheckinsSchema,
  startDate: newBadgeRewardStartDateSchema.required('Must enter a start date'),
  startTime: newBadgeRewardStartTimeSchema.required('Must enter a start time'),
  endDate: newBadgeRewardEndDateSchema,
  endTime: newBadgeRewardEndTimeSchema,
  schedules: badgeRewardSchedulesSchema,
  licensedEstablishmentIds: badgeRewardLicensedEstablishmentIdsSchema,
  corporateAccountId: badgeRewardCorporateAccountIdSchema,
  organizationId: badgeRewardOrganizationIdSchema,
  rewardsProgramId: badgeRewardsProgramId,
})

const defaultRenewableOptions: BadgeRewardRenewableType[] = [
  'No',
  'Daily',
  'Weekly',
  'Monthly',
  'Quarterly',
  'Yearly',
]

const checkInStreakRenewableOptions: BadgeRewardRenewableType[] = [
  'No',
  'LocationCheckInStreak',
]

const getRenewableOptions = (
  type: BadgeRewardType
): BadgeRewardRenewableType[] =>
  type === 'LocationCheckInStreak'
    ? checkInStreakRenewableOptions
    : defaultRenewableOptions

const getValidRenewableValue = (
  type: BadgeRewardType,
  renewableType?: BadgeRewardRenewableType | null
): BadgeRewardRenewableType => {
  const renewableOptions = getRenewableOptions(type)

  return renewableType && renewableOptions.includes(renewableType)
    ? renewableType
    : 'No'
}

const isLocationBasedSubtype = (subtype?: string) => subtype === 'Location'

export const LocationBadgesNewPage = () => {
  const EventIconComponent = resolveIconComponent(EventIcon)
  const AccessTimeIconComponent = resolveIconComponent(AccessTimeIcon)
  const RewardProgramIconComponent = resolveIconComponent(RewardProgramIcon)
  const CloseIconComponent = resolveIconComponent(CloseIcon)
  const [selectedScheduleBadgeReward, setSelectedScheduleBadgeReward] =
    useState('No')
  const [limitCheckins, setLimitCheckins] = useState(false)

  const [rewardProgramSearchQuery, setRewardProgramSearchQuery] = useState('')
  const [selectedRewardProgram, setSelectedRewardProgram] =
    useState<RewardsProgram>()
  const debouncedRewardProgramSearchQuery = useDebounce(
    rewardProgramSearchQuery,
    500
  )

  const formMethods = useForm({
    resolver: yupResolver(BadgeRewardSchema),
    defaultValues: {
      limitCheckins: false,
    },
  })
  const {
    register,
    handleSubmit,
    control,
    setValue,
    formState: { errors },
  } = formMethods

  const subtype = useWatch({ control, name: 'subtype' })
  const selectedLicensedEstablishmentIdsField = useWatch({
    control,
    name: 'licensedEstablishmentIds',
  })
  const selectedCorporateAccountId = useWatch({
    control,
    name: 'corporateAccountId',
  })
  const selectedOrganizationId = useWatch({ control, name: 'organizationId' })

  const { post } = useApiClient()
  const setMessage = useSnackbar((state) => state.setMessage)
  const allLocationBadgesQuery = useGetAllLocationBadges()
  const locationBadges = allLocationBadgesQuery.data
  const navigate = useNavigate()
  const { isMobile } = useLayout()

  const [selectedBadgeRewardType, setSelectedBadgeRewardType] =
    useState<BadgeRewardType>('LocationCheckIn')

  const [selectedBadgeRewardRenewable, setSelectedBadgeRewardRenewable] =
    useState<BadgeRewardRenewableType>('No')

  const [selectedBadgeRewardDependent, setselectedBadgeRewardDependent] =
    useState<number>(0)
  const [locationPointsAwarded, setLocationPointsAwarded] = useState(0)
  const [
    locationSweepstakeEntriesAwarded,
    setLocationSweepstakeEntriesAwarded,
  ] = useState(0)
  const [
    selectedLocationSweepstakeContestId,
    setSelectedLocationSweepstakeContestId,
  ] = useState<number>(0)
  const [
    selectedLocationSweepstakeGroupId,
    setSelectedLocationSweepstakeGroupId,
  ] = useState<string>('')
  const [
    locationSweepstakeSelectionError,
    setLocationSweepstakeSelectionError,
  ] = useState<string | null>(null)
  const [rewardProgramSelectionError, setRewardProgramSelectionError] =
    useState<string | null>(null)
  const [count, setCount] = useState(0)
  const [countValidationError, setCountValidationError] = useState<
    string | null
  >(null)

  // Fetch location-scoped sweepstakes based on the selected licensed establishments, corporate account, and organization
  const {
    regularLocationSweepstakes,
    groupedLocationSweepstakes,
    isPending: locationSweepstakesPending,
  } = useLocationScopedSweepstakes({
    subtype,
    licensedEstablishmentIds: selectedLicensedEstablishmentIdsField,
    corporateAccountId: selectedCorporateAccountId,
    organizationId: selectedOrganizationId,
  })

  useEffect(() => {
    if (locationSweepstakeEntriesAwarded <= 0) {
      setSelectedLocationSweepstakeContestId(0)
      setSelectedLocationSweepstakeGroupId('')
      setLocationSweepstakeSelectionError(null)
    }
  }, [locationSweepstakeEntriesAwarded])

  useEffect(() => {
    if (
      selectedLocationSweepstakeContestId > 0 &&
      !regularLocationSweepstakes.some(
        (sweepstake) =>
          Number(sweepstake.id) === selectedLocationSweepstakeContestId
      )
    ) {
      setSelectedLocationSweepstakeContestId(0)
    }

    if (
      selectedLocationSweepstakeGroupId.length > 0 &&
      !groupedLocationSweepstakes.some(
        (sweepstake) => sweepstake.groupId === selectedLocationSweepstakeGroupId
      )
    ) {
      setSelectedLocationSweepstakeGroupId('')
    }
  }, [
    regularLocationSweepstakes,
    groupedLocationSweepstakes,
    selectedLocationSweepstakeContestId,
    selectedLocationSweepstakeGroupId,
  ])

  useEffect(() => {
    if (locationPointsAwarded <= 0) {
      setRewardProgramSearchQuery('')
      setSelectedRewardProgram(undefined)
      setValue('rewardsProgramId', null, { shouldDirty: true })
      setRewardProgramSelectionError(null)
    }
  }, [locationPointsAwarded, setValue])

  const searchRewardPrograms = useTypedApiClient({
    path: '/admin/rewards-programs/search',
    method: 'get',
  })

  const rewardProgramsQuery = useQuery<RewardsProgram[], ApiError>({
    queryKey: ['rewardPrograms', debouncedRewardProgramSearchQuery],
    queryFn: async () => {
      if (debouncedRewardProgramSearchQuery.length >= 2) {
        return (
          await searchRewardPrograms({
            query: debouncedRewardProgramSearchQuery,
          })
        ).data
      }

      return []
    },
  })

  const filteredRewardPrograms = rewardProgramsQuery.data?.filter(
    (rewardProgram) => rewardProgram.type !== 'Global' && rewardProgram.active
  )

  const manageBadgeLEs = useManageBadgeLEs({
    onError: (error: ApiError) => {
      console.error('Error assigning locations to badge reward', error)
      const apiErrorMessage =
        typeof error.data === 'string'
          ? error.data
          : (error.data as { message?: string } | undefined)?.message

      setMessage(
        apiErrorMessage ?? 'Error assigning locations to badge reward',
        'error',
        {
          vertical: 'bottom',
          horizontal: 'left',
        }
      )
    },
  })

  const createBadgeReward = useMutation({
    mutationKey: ['badgerewards/create'],
    mutationFn: async (data: FieldValues) => {
      const badgeRewardSchedules: BadgeRewardSchedule[] = []
      data.schedules?.forEach((schedule: BadgeRewardSchedule) => {
        if (schedule.opening && schedule.closing) {
          badgeRewardSchedules.push({
            ...schedule,
          })
        }
      })

      const response = (await post('/admin/badgerewards', {
        params: { header: undefined },
        body: {
          category: 'LocationBadge',
          type: data.type,
          subtype: data.subtype as BadgeSubtype,
          name: data.name,
          description: data.description,
          startDate: data.startDate
            ? formatISO(parse(data.startTime, 'hh:mm a', data.startDate))
            : null,
          endDate: data.endDate
            ? formatISO(parse(data.endTime, 'hh:mm a', data.endDate))
            : null,
          badgeRewardSchedules,
          renewableType: selectedBadgeRewardRenewable,
          dependentBadgeRewardId: Number(data.dependsOnBadgeRewardId) || 0,
          locationPointsAwarded,
          locationSweepstakeEntriesAwarded,
          locationSweepstakeContestId:
            locationSweepstakeEntriesAwarded > 0 &&
            selectedLocationSweepstakeContestId > 0
              ? selectedLocationSweepstakeContestId
              : null,
          locationSweepstakeGroupId:
            locationSweepstakeEntriesAwarded > 0 &&
            selectedLocationSweepstakeGroupId.length > 0
              ? selectedLocationSweepstakeGroupId
              : null,
          rewardsProgramId: selectedRewardProgram
            ? Number(selectedRewardProgram.id)
            : null,
          count,
          limitCheckins,
          licensedEstablishmentIds:
            Array.isArray(data.licensedEstablishmentIds) &&
            data.licensedEstablishmentIds.length > 0
              ? data.licensedEstablishmentIds.filter(
                  (id: number | undefined): id is number =>
                    typeof id === 'number'
                )
              : null,
          corporateAccountId:
            data.subtype === 'CorporateAccount'
              ? Number(data.corporateAccountId)
              : null,
          organizationId:
            data.subtype === 'Organization'
              ? Number(data.organizationId)
              : null,
        },
      })) as {
        error?: { message?: string }
        data?: { id?: number }
      }

      if (response.error !== undefined) {
        throw new Error(
          response.error?.message ?? 'Error creating badge reward'
        )
      }

      return response
    },
    onSuccess: async (response, variables) => {
      const badgeRewardId = Number(response.data?.id)

      const licensedEstablishmentIds: number[] = Array.isArray(
        variables.licensedEstablishmentIds
      )
        ? variables.licensedEstablishmentIds.filter(
            (id: number | undefined): id is number => typeof id === 'number'
          )
        : []

      if (licensedEstablishmentIds.length > 0) {
        const req: BadgeLEDTO[] = licensedEstablishmentIds.map(
          (licensedEstablishmentId) => ({
            badgeRewardId,
            licensedEstablishmentId,
          })
        )

        try {
          await manageBadgeLEs.mutateAsync({
            badgeRewardId,
            req,
          })
        } catch {
          // manageBadgeLEs' own onError already displays the specific message;
          // stop here so createBadgeReward's onError doesn't overwrite it
          return
        }
      }

      setMessage('Badge Reward was successfully created.', 'success', {
        vertical: 'top',
        horizontal: 'right',
      })
      navigate(`/Contests/LocationBadges/${badgeRewardId}`)
    },
    onError: (error: Error) => {
      console.error('Error creating badge reward', error)

      setMessage(error.message || 'Error creating badge reward', 'error', {
        vertical: 'bottom',
        horizontal: 'left',
      })
    },
  })

  if (
    createBadgeReward.isPending ||
    allLocationBadgesQuery.isPending ||
    locationSweepstakesPending
  ) {
    return <ActivityIndicator />
  }

  const renewableOptions = getRenewableOptions(selectedBadgeRewardType)

  // These fields aren't tracked by the yup schema, so they must be validated
  // outside of handleSubmit or their errors get silently skipped when other fields are also invalid
  const validateManualFields = () => {
    let isValid = true

    if (
      badgeRewardTypeHasAccessToControl(selectedBadgeRewardType, 'Count') &&
      count < 1
    ) {
      setCountValidationError('Count must be at least 1')
      isValid = false
    } else {
      setCountValidationError(null)
    }

    if (
      locationSweepstakeEntriesAwarded > 0 &&
      selectedLocationSweepstakeContestId === 0 &&
      selectedLocationSweepstakeGroupId.length === 0
    ) {
      setLocationSweepstakeSelectionError(
        'Must select either a regular location sweepstake or a multi draw sweepstake'
      )
      isValid = false
    } else {
      setLocationSweepstakeSelectionError(null)
    }

    if (locationPointsAwarded > 0 && !selectedRewardProgram) {
      setRewardProgramSelectionError('Must select a rewards program')
      isValid = false
    } else {
      setRewardProgramSelectionError(null)
    }

    return isValid
  }

  const submitBadgeReward = handleSubmit((data) => {
    createBadgeReward.mutate(data)
  })

  const createBadgeRewardWrapper = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (!validateManualFields()) {
      return
    }

    void submitBadgeReward(event)
  }

  const handleTypeChange = (e: SelectChangeEvent) => {
    const nextType = e.target.value as BadgeRewardType

    setSelectedScheduleBadgeReward('No')
    setSelectedBadgeRewardType(nextType)
    setSelectedBadgeRewardRenewable((currentRenewable) =>
      getValidRenewableValue(nextType, currentRenewable)
    )
  }

  const handleRenewableChange = (e: SelectChangeEvent) => {
    setSelectedBadgeRewardRenewable(e.target.value as BadgeRewardRenewableType)
  }

  const handleDependentBadgeRewardChange = (e: SelectChangeEvent) => {
    setselectedBadgeRewardDependent(Number(e.target.value))
  }

  const handleRegularLocationSweepstakeChange = (e: SelectChangeEvent) => {
    const nextContestId = Number(e.target.value)
    setSelectedLocationSweepstakeContestId(nextContestId)
    if (nextContestId > 0) {
      setSelectedLocationSweepstakeGroupId('')
    }
    if (locationSweepstakeSelectionError) {
      setLocationSweepstakeSelectionError(null)
    }
  }

  const handleGroupedLocationSweepstakeChange = (e: SelectChangeEvent) => {
    const nextGroupId = String(e.target.value)
    setSelectedLocationSweepstakeGroupId(nextGroupId)
    if (nextGroupId.length > 0) {
      setSelectedLocationSweepstakeContestId(0)
    }
    if (locationSweepstakeSelectionError) {
      setLocationSweepstakeSelectionError(null)
    }
  }
  const handleRewardProgramClick = (rewardProgram: RewardsProgram) => {
    setSelectedRewardProgram(rewardProgram)
    setValue('rewardsProgramId', Number(rewardProgram.id), {
      shouldDirty: true,
    })
    setRewardProgramSelectionError(null)
  }

  const onCloseRewardProgram = () => {
    setRewardProgramSearchQuery('')
    setSelectedRewardProgram(undefined)
    setValue('rewardsProgramId', null, { shouldDirty: true })
  }

  return (
    <Page
      header={
        <PageHeader
          title={'Create a New Location Badge'}
          subtitle={
            'You must fill out every field in the form before clicking the submit button.\nIncomplete submissions cannot be processed.'
          }
          backText="Location Badges"
          backPath="/Contests/LocationBadges"
          isSecondary={true}
        />
      }
    >
      <FormProvider {...formMethods}>
        <form onSubmit={createBadgeRewardWrapper} noValidate>
          <Grid item xs={12} pb={1}>
            {Object.keys(errors).length > 0 && (
              <Alert severity="error">Please correct errors to continue</Alert>
            )}
          </Grid>
          <Stack spacing={4} maxWidth={isMobile ? '90vw' : undefined}>
            <Box>
              <TextField
                id="badge-reward-name-text-box"
                required
                error={isPresent(errors.name)}
                label="Name"
                placeholder="Enter badge reward name"
                {...register('name')}
              />
              <Typography variant="body-3" component={'p'} mt={1}>
                50 characters max
              </Typography>
              {errors.name?.message && (
                <FormFieldErrorMessage message={errors.name.message} />
              )}
            </Box>
            <Box
              sx={{
                maxWidth: '520px',
              }}
            >
              <Box>
                <InputLabel required sx={{ fontWeight: 'bold' }} id="type">
                  Type
                </InputLabel>
              </Box>
              <Box>
                <Select
                  fullWidth
                  defaultValue={'LocationCheckIn'}
                  error={isPresent(errors.type)}
                  value={selectedBadgeRewardType}
                  {...register('type')}
                  onChange={handleTypeChange}
                >
                  <MenuItem value="LocationCheckIn">Location Check-In</MenuItem>
                  <MenuItem value="LocationCheckInStreak">
                    Location Check-In Streak
                  </MenuItem>
                  <MenuItem value="UniqueLocationVisits">
                    Unique Location Visits
                  </MenuItem>
                </Select>
                {errors.type?.message && (
                  <FormFieldErrorMessage message={errors.type.message} />
                )}
              </Box>
            </Box>

            <Box
              sx={{
                maxWidth: '520px',
              }}
            >
              <Box>
                <InputLabel required sx={{ fontWeight: 'bold' }} id="subtype">
                  Subtype
                </InputLabel>
              </Box>
              <Box>
                <LocationBadgeTypeSelect
                  name="subtype"
                  data-testid="LocationBadgeTypeSelect"
                  types={['Location', 'CorporateAccount', 'Organization']}
                  disabled={false}
                />

                {errors.subtype?.message && (
                  <FormFieldErrorMessage message={errors.subtype?.message} />
                )}
              </Box>
            </Box>

            {subtype === 'Location' && (
              <Box>
                <LocationSelect
                  required
                  showLabel
                  name="licensedEstablishmentIds"
                  data-testid="LocationSelect"
                />
                {isLocationBasedSubtype(subtype) &&
                  errors.licensedEstablishmentIds?.message && (
                    <FormFieldErrorMessage
                      message={errors.licensedEstablishmentIds?.message}
                    />
                  )}
              </Box>
            )}

            {subtype === 'CorporateAccount' && (
              <Box>
                <SelectCorporateAccount
                  data-testid="SelectCorporateAccount"
                  required
                  error={isPresent(errors.corporateAccountId)}
                  {...register('corporateAccountId')}
                  activityIndicatorMaxWidth="520px"
                  label="Corporate Account"
                  fullWidth
                  corporateAccountIdKey="corporateAccountId"
                  placeholder="Select a Corporate Account"
                />
                {subtype === 'CorporateAccount' &&
                  errors.corporateAccountId?.message && (
                    <FormFieldErrorMessage
                      message={errors.corporateAccountId?.message}
                    />
                  )}
              </Box>
            )}

            {subtype === 'Organization' && (
              <Box>
                <SelectFromAllOrganizations
                  {...register('organizationId')}
                  activityIndicatorMaxWidth="520px"
                  required
                  error={isPresent(errors.organizationId)}
                  label="Organization"
                  fullWidth
                  organizationIdKey="organizationId"
                  placeholder="Select an Organization"
                />
                {subtype === 'Organization' &&
                  errors.organizationId?.message && (
                    <FormFieldErrorMessage
                      message={errors.organizationId?.message}
                    />
                  )}
              </Box>
            )}

            <Box>
              <TextField
                multiline
                rows={3}
                required
                error={isPresent(errors.description)}
                label="Description"
                placeholder="Enter description of badge reward"
                {...register('description')}
              />
              <Typography variant="body-3" component={'p'} mt={1}>
                500 characters max
              </Typography>
              {errors.description?.message && (
                <FormFieldErrorMessage message={errors.description.message} />
              )}
            </Box>

            {badgeRewardTypeHasAccessToControl(
              selectedBadgeRewardType,
              'StartDateTime'
            ) && (
              <Box sx={{ maxWidth: '520px' }}>
                <InputLabel required htmlFor="startDate">
                  Start Date/Time
                </InputLabel>
                <Stack direction="row" spacing={2}>
                  <Box sx={{ flex: 1 }}>
                    <HookableDateField
                      sx={{ width: '100%' }}
                      slotProps={{
                        textField: {
                          InputProps: {
                            endAdornment: (
                              <InputAdornment position="end">
                                <EventIconComponent />
                              </InputAdornment>
                            ),
                          },
                          error: isPresent(errors.startDate),
                        },
                      }}
                      {...register('startDate')}
                    />
                    {errors.startDate?.message && (
                      <FormFieldErrorMessage
                        message={errors.startDate.message}
                      />
                    )}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <HookableTimeField
                      {...register('startTime')}
                      slotProps={{
                        textField: {
                          InputProps: {
                            endAdornment: (
                              <InputAdornment position="end">
                                <AccessTimeIconComponent />
                              </InputAdornment>
                            ),
                          },
                          error: isPresent(errors.startTime),
                        },
                      }}
                      sx={{ width: '100%' }}
                    />
                    {errors.startTime?.message && (
                      <FormFieldErrorMessage
                        message={errors.startTime.message}
                      />
                    )}
                  </Box>
                </Stack>
              </Box>
            )}

            {badgeRewardTypeHasAccessToControl(
              selectedBadgeRewardType,
              'EndDateTime'
            ) && (
              <Box sx={{ maxWidth: '520px' }}>
                <InputLabel htmlFor="endDate">End Date/Time</InputLabel>
                <Stack direction="row" spacing={2}>
                  <Box sx={{ flex: 1 }}>
                    <HookableDateField
                      sx={{ width: '100%' }}
                      slotProps={{
                        textField: {
                          InputProps: {
                            endAdornment: (
                              <InputAdornment position="end">
                                <EventIconComponent />
                              </InputAdornment>
                            ),
                          },
                          error: isPresent(errors.endDate),
                        },
                      }}
                      {...register('endDate')}
                    />
                    {errors.endDate?.message && (
                      <FormFieldErrorMessage message={errors.endDate.message} />
                    )}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <HookableTimeField
                      {...register('endTime')}
                      slotProps={{
                        textField: {
                          InputProps: {
                            endAdornment: (
                              <InputAdornment position="end">
                                <AccessTimeIconComponent />
                              </InputAdornment>
                            ),
                          },
                          error: isPresent(errors.endTime),
                        },
                      }}
                      sx={{ width: '100%' }}
                    />
                    {errors.endTime?.message && (
                      <FormFieldErrorMessage message={errors.endTime.message} />
                    )}
                  </Box>
                </Stack>
              </Box>
            )}

            {badgeRewardTypeHasAccessToControl(
              selectedBadgeRewardType,
              'Renewable'
            ) && (
              <Box
                sx={{
                  maxWidth: '520px',
                }}
              >
                <Box>
                  <InputLabel
                    htmlFor="renewable"
                    sx={{ fontSize: '16px', fontWeight: '100' }}
                  >
                    Renewable
                  </InputLabel>
                </Box>
                <Box>
                  <Select
                    {...register('renewable', { required: true })}
                    inputProps={{ id: 'renewableType' }}
                    error={isPresent(errors.renewable)}
                    value={selectedBadgeRewardRenewable}
                    sx={{ width: '100%', maxWidth: '520px' }}
                    onChange={handleRenewableChange}
                  >
                    {renewableOptions.map((renewableOption) => (
                      <MenuItem key={renewableOption} value={renewableOption}>
                        {renewableOption}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
              </Box>
            )}

            {badgeRewardTypeHasAccessToControl(
              selectedBadgeRewardType,
              'DependentOnBadge'
            ) && (
              <Box
                sx={{
                  maxWidth: '520px',
                }}
              >
                <Box>
                  <InputLabel
                    htmlFor="dependentBadgeRewardId"
                    sx={{ fontSize: '16px', fontWeight: '100' }}
                  >
                    Dependent on badge reward
                  </InputLabel>
                </Box>
                <Box>
                  <Select
                    {...register('dependsOnBadgeRewardId', {
                      required: false,
                    })}
                    inputProps={{ id: 'dependsOnBadgeRewardId' }}
                    error={isPresent(errors.dependsOnBadgeRewardId)}
                    value={String(selectedBadgeRewardDependent)}
                    sx={{ width: '100%', maxWidth: '520px' }}
                    onChange={handleDependentBadgeRewardChange}
                  >
                    <MenuItem value={0}>None</MenuItem>
                    {locationBadges?.map((x) => (
                      <MenuItem key={Number(x.id)} value={Number(x.id)}>
                        {x.name}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
              </Box>
            )}

            {badgeRewardTypeHasAccessToControl(
              selectedBadgeRewardType,
              'LocationPointsToAward'
            ) && (
              <Box>
                <InputLabel
                  htmlFor="locationPointsAwarded"
                  sx={{ fontSize: '16px', fontWeight: '100', mb: 1 }}
                >
                  Location Points to award on Badge earned
                </InputLabel>

                <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                  <Counter
                    amount={locationPointsAwarded}
                    setAmount={setLocationPointsAwarded}
                    minimumAmount={0}
                  />
                </Box>
              </Box>
            )}

            {badgeRewardTypeHasAccessToControl(
              selectedBadgeRewardType,
              'RewardsProgramId'
            ) &&
              locationPointsAwarded > 0 && (
                <Box
                  sx={{
                    maxWidth: '520px',
                  }}
                >
                  <Box>
                    <InputLabel
                      required
                      htmlFor="rewardsProgramId"
                      sx={{ fontSize: '16px', fontWeight: '100' }}
                    >
                      Rewards Program
                    </InputLabel>
                  </Box>
                  <Box>
                    <Typography
                      variant="body-2"
                      color={'rgba(44, 44, 39, 0.68)'}
                    >
                      Make sure to use a minimum of at least 2 characters per
                      word when searching.
                    </Typography>
                    <SearchField
                      placeholder="Start typing to search"
                      value={rewardProgramSearchQuery}
                      onChange={(e) =>
                        setRewardProgramSearchQuery(e.target.value)
                      }
                      fullWidth
                      sx={{ my: '16px' }}
                    />
                    {!selectedRewardProgram &&
                      !!filteredRewardPrograms?.length && (
                        <Paper variant="outlined" sx={{ mt: '7px' }}>
                          {filteredRewardPrograms.map((rewardProgram) => {
                            let resultString = `${rewardProgram.name} - ${rewardProgram.id}`
                            resultString = resultString.replace(
                              rewardProgramSearchQuery,
                              `<strong>${rewardProgramSearchQuery}</strong>`
                            )

                            return (
                              <MenuItem
                                key={rewardProgram.id}
                                value={rewardProgram.id}
                                onClick={() =>
                                  handleRewardProgramClick(rewardProgram)
                                }
                              >
                                <p
                                  dangerouslySetInnerHTML={{
                                    __html: resultString,
                                  }}
                                />
                              </MenuItem>
                            )
                          })}
                        </Paper>
                      )}
                    {selectedRewardProgram && (
                      <Box
                        sx={{
                          backgroundColor: colorPrimitives.lightGray,
                          p: '12px',
                        }}
                        maxWidth={'520px'}
                      >
                        <div className="flex justify-between items-center">
                          <div className="flex flex-row items-center">
                            <SvgIcon
                              sx={{
                                color: colors.text.primary,
                                transform: 'translate(0, -2px)',
                                height: '40px',
                                width: '40px',
                                marginRight: '10px',
                              }}
                            >
                              {RewardProgramIconComponent ? (
                                <RewardProgramIconComponent />
                              ) : null}
                            </SvgIcon>
                            <div className="flex flex-col">
                              <Typography variant="body-1">
                                {selectedRewardProgram.name}
                              </Typography>
                              <Typography
                                variant="body-1"
                                color="text.secondary"
                              >
                                {selectedRewardProgram.id}
                              </Typography>
                            </div>
                          </div>
                          <Box
                            onClick={onCloseRewardProgram}
                            sx={{
                              '&:hover': {
                                cursor: 'pointer',
                              },
                            }}
                          >
                            <SvgIcon
                              sx={{
                                transform: 'translate(0, -2px)',
                                height: '24px',
                                width: '24px',
                              }}
                            >
                              {CloseIconComponent ? (
                                <CloseIconComponent />
                              ) : null}
                            </SvgIcon>
                          </Box>
                        </div>
                      </Box>
                    )}
                    <input type="hidden" {...register('rewardsProgramId')} />
                    {rewardProgramSelectionError && (
                      <FormFieldErrorMessage
                        message={rewardProgramSelectionError}
                      />
                    )}
                  </Box>
                </Box>
              )}

            {badgeRewardTypeHasAccessToControl(
              selectedBadgeRewardType,
              'LocationSweepstakeEntriesToAward'
            ) && (
              <Box>
                <InputLabel
                  htmlFor="locationSweepstakeEntriesAwarded"
                  sx={{ fontSize: '16px', fontWeight: '100', mb: 1 }}
                >
                  Location Sweepstake Entries to award on Badge earned
                </InputLabel>

                <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                  <Counter
                    amount={locationSweepstakeEntriesAwarded}
                    setAmount={setLocationSweepstakeEntriesAwarded}
                    minimumAmount={0}
                  />
                </Box>

                {locationSweepstakeEntriesAwarded > 0 && (
                  <Stack spacing={2} mt={2} maxWidth="520px">
                    <Box>
                      <InputLabel
                        htmlFor="locationSweepstakeContestId"
                        sx={{ fontSize: '16px', fontWeight: '100' }}
                      >
                        Regular Location Sweepstake
                      </InputLabel>
                      <Select
                        id="locationSweepstakeContestId"
                        value={String(selectedLocationSweepstakeContestId)}
                        onChange={handleRegularLocationSweepstakeChange}
                        fullWidth
                        disabled={selectedLocationSweepstakeGroupId.length > 0}
                      >
                        <MenuItem value="0">None</MenuItem>
                        {regularLocationSweepstakes.map((sweepstake) => (
                          <MenuItem
                            key={sweepstake.id}
                            value={String(sweepstake.id)}
                          >
                            {sweepstake.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </Box>

                    <Box>
                      <InputLabel
                        htmlFor="locationSweepstakeGroupId"
                        sx={{ fontSize: '16px', fontWeight: '100' }}
                      >
                        Multi Draw Location Sweepstake
                      </InputLabel>
                      <Select
                        id="locationSweepstakeGroupId"
                        value={selectedLocationSweepstakeGroupId}
                        onChange={handleGroupedLocationSweepstakeChange}
                        fullWidth
                        displayEmpty
                        disabled={selectedLocationSweepstakeContestId > 0}
                      >
                        <MenuItem value="">None</MenuItem>
                        {groupedLocationSweepstakes.map((sweepstake) => (
                          <MenuItem
                            key={sweepstake.groupId ?? sweepstake.id}
                            value={String(sweepstake.groupId)}
                          >
                            {sweepstake.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </Box>

                    {locationSweepstakeSelectionError && (
                      <FormFieldErrorMessage
                        message={locationSweepstakeSelectionError}
                      />
                    )}
                  </Stack>
                )}
              </Box>
            )}

            {badgeRewardTypeHasAccessToControl(
              selectedBadgeRewardType,
              'Count'
            ) && (
              <Box>
                <InputLabel
                  htmlFor="count"
                  sx={{ fontSize: '16px', fontWeight: '100' }}
                >
                  {getBadgeRewardTypeName(selectedBadgeRewardType)} Count
                </InputLabel>
                <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                  <Counter
                    amount={count}
                    setAmount={setCount}
                    minimumAmount={0}
                  />
                </Box>
                {countValidationError && (
                  <FormFieldErrorMessage message={countValidationError} />
                )}
              </Box>
            )}

            {badgeRewardTypeHasAccessToControl(
              selectedBadgeRewardType,
              'LimitCheckins'
            ) && (
              <Stack direction="column" width={'500px'}>
                <InputLabel
                  htmlFor={'limitCheckins'}
                  sx={isMobile ? { fontSize: '16px' } : {}}
                >
                  Limit to 1 Check-in Per Day?
                </InputLabel>
                <Select
                  data-testid="limitCheckinsBoolean"
                  defaultValue="false"
                  value={limitCheckins ? 'true' : 'false'}
                  onChange={(e) => setLimitCheckins(e.target.value === 'true')}
                >
                  <MenuItem value="false">No</MenuItem>
                  <MenuItem value="true">Yes</MenuItem>
                </Select>
              </Stack>
            )}

            {badgeRewardTypeHasAccessToControl(
              selectedBadgeRewardType,
              'Schedule'
            ) && (
              <Stack direction="column" width={'500px'}>
                <InputLabel
                  htmlFor={'scheduledBoolean'}
                  sx={isMobile ? { fontSize: '16px' } : {}}
                >
                  Do you want to schedule this Badge?
                </InputLabel>
                <Select
                  data-testid="scheduledBoolean"
                  defaultValue="No"
                  value={selectedScheduleBadgeReward}
                  onChange={(e) =>
                    setSelectedScheduleBadgeReward(e.target.value)
                  }
                >
                  <MenuItem value="No">No</MenuItem>
                  <MenuItem value="Yes">Yes</MenuItem>
                </Select>
              </Stack>
            )}

            {selectedScheduleBadgeReward === 'Yes' && (
              <Box className="pt-1">
                <Typography variant="subtitle-1" component={'p'}>
                  Badge Schedule
                </Typography>
                <Typography variant="body-3">
                  {`Select the days you'd like the badge to be available to patrons`}
                </Typography>
                <Stack spacing={2} mt="20px">
                  {daysOfWeek.map((day, index) => {
                    return (
                      <Box
                        key={day}
                        style={{ height: '85px' }}
                        height={'85'}
                        className="flex flex-col justify-center"
                      >
                        <ScheduleRow
                          day={day}
                          index={index}
                          key={day}
                          defaultValues={{
                            opening: '',
                            closing: '',
                            patronCheckInPointBonusMultiplier: 0,
                          }}
                          openLabel="Enabled"
                          closedLabel="Disabled"
                        />
                      </Box>
                    )
                  })}
                </Stack>
              </Box>
            )}

            {isMobile && (
              <Grid container spacing={2} maxWidth="90vw">
                <Grid
                  item
                  xs={6}
                  style={{ paddingLeft: 0, paddingRight: '8px' }}
                >
                  <RouterLink to="/Contests/LocationBadges">
                    <Button variant="outlined" fullWidth>
                      Cancel
                    </Button>
                  </RouterLink>
                </Grid>
                <Grid
                  item
                  xs={6}
                  style={{ paddingLeft: '8px', paddingRight: 0 }}
                >
                  <Button type="submit" variant="contained" fullWidth>
                    Create
                  </Button>
                </Grid>
              </Grid>
            )}
            {!isMobile && (
              <Stack direction="row" spacing={2}>
                <RouterLink to="/Contests/LocationBadges">
                  <Button variant="outlined">Cancel</Button>
                </RouterLink>
                <Button type="submit" variant="contained">
                  Create
                </Button>
              </Stack>
            )}
          </Stack>
        </form>
      </FormProvider>
    </Page>
  )
}
